import { useContext, useMemo } from 'react';

import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';

import { FieldConfig } from '../config';
import { ComparisonRow } from '../types';
import { formatValueForDisplay } from '../utils';

type ComparisonGridProps = {
  comparisonData: ComparisonRow[];
  fields: Record<string, FieldConfig>;
  getRowDisplayName?: (row: any) => string;
};

export const ComparisonGrid = ({
  comparisonData,
  fields,
  getRowDisplayName,
}: ComparisonGridProps) => {
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);

  const columnDefs = useMemo<ColDef[]>(() => {
    const displayNameColumn: ColDef = {
      headerName: t('common.name'),
      field: 'displayName',
      pinned: 'left',
      width: 100,
      cellRenderer: (params: any) => {
        const rowData = params.data as ComparisonRow;
        const displayName = getRowDisplayName
          ? getRowDisplayName(rowData.row)
          : rowData.row.name || rowData.row.id;
        return <div className="font-semibold">{displayName}</div>;
      },
    };

    if (comparisonData.length === 0) return [displayNameColumn];

    const firstRow = comparisonData[0];
    if (firstRow.fields.length === 0) return [displayNameColumn];

    const allFieldNames = new Set<string>();
    comparisonData.forEach((row) => {
      row.fields.forEach((field) => {
        allFieldNames.add(field.fieldName);
      });
    });

    const formatFieldName = (name: string): string => {
      return name
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    };

    const fieldColumns: ColDef[] = Array.from(allFieldNames).map(
      (fieldName) => {
        const fieldConfig = fields[fieldName];
        return {
          headerName: fieldConfig?.label
            ? t(fieldConfig.label)
            : formatFieldName(fieldName),
          field: fieldName,
          cellRenderer: (params: any) => {
            const rowData = params.data as ComparisonRow;
            const fieldData = rowData.fields.find(
              (f) => f.fieldName === fieldName,
            );
            if (!fieldData) return '';

            const fieldConfigForFormat = fields[fieldName];

            return (
              <div className="flex gap-4">
                <div
                  className={
                    fieldData.hasChanged ? 'rounded bg-red-100 p-2' : 'p-2'
                  }
                >
                  <div className="text-xs text-gray-500">{t('wizard.old')}</div>
                  <div>
                    {formatValueForDisplay(
                      fieldData.oldValue,
                      fieldName,
                      fieldConfigForFormat,
                    )}
                  </div>
                </div>
                <div
                  className={
                    fieldData.hasChanged ? 'rounded bg-green-100 p-2' : 'p-2'
                  }
                >
                  <div className="text-xs text-gray-500">{t('wizard.new')}</div>
                  <div>
                    {formatValueForDisplay(
                      fieldData.newValue,
                      fieldName,
                      fieldConfigForFormat,
                    )}
                  </div>
                </div>
              </div>
            );
          },
          flex: 1,
          minWidth: 200,
        };
      },
    );
    return [displayNameColumn, ...fieldColumns];
  }, [comparisonData, fields, getRowDisplayName, t]);

  return (
    <div className="w-full" style={{ height: '60vh', minHeight: '400px' }}>
      <AgGridReact
        gridOptions={defaultGridOptions}
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
        rowData={comparisonData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.rowId}
        rowHeight={80}
        suppressRowClickSelection
      />
    </div>
  );
};
